import { getModule } from 'vuex-module-decorators';
import uploadService from '../service/upload';
import { Store } from 'vuex';
import { v4 as uuid } from 'uuid';
import UploadModule from './modules/Upload';
import ImageProcessingError from '@/model/ImageProcessingError';

const asUrl = (file: Blob) => URL.createObjectURL(file);

export default {
    init(store: Store<any>) {
        const uploadModule = getModule(UploadModule, store);

        uploadService.conflicts$.subscribe((conflict) => {
            if (!conflict.photo.active) return;
            const smallThumb = asUrl(conflict.smallThumb);
            const largeThumb = asUrl(conflict.watermarked);
            uploadModule.addConflict({
                ...conflict,
                photo: {
                    filename: conflict.photo.file.name,
                    file: conflict.photo.file,
                },
                smallThumb,
                largeThumb,
                conflictId: uuid(),
            });
        });
        uploadService.params$.subscribe((params) => uploadModule.setPlaceholderParams({
            file: params.photo.file,
            sessionId: params.photo.sessionId,
            params
        }));
        uploadService.smallThumbs$.subscribe(({ photo, smallThumb }) => uploadModule.setPlaceholderSmallThumb({
            sessionId: photo.sessionId,
            file: photo.file,
            smallThumb,
        }));
        uploadService.watermark$.subscribe(({ photo, watermarked }) => uploadModule.setPlaceholderLargeThumb({
            file: photo.file,
            sessionId: photo.sessionId,
            largeThumb: watermarked,
        }));
        uploadService.hashes$.subscribe(({ photo, perceptual, data }) => uploadModule.setPlaceholderHashes({
            sessionId: photo.sessionId,
            file: photo.file,
            dataHash: data,
            perceptualHash: perceptual,
        }));
        uploadService.uploads$.subscribe(async payload => {
            if (payload.cancelled || !payload.photo.active) return;
            try {
                await uploadModule.uploadFinished({
                    sessionId: payload.photo.sessionId, file: payload.photo.file,
                    smallThumbUrl: payload.smallThumbUrl, largeThumbUrl: payload.largeThumbUrl,
                    signal: payload.photo.signal,
                });
                uploadService.finish(payload.photo);
            } catch (error) { uploadService.fail(payload.photo, error); }
        });
        uploadService.errors$.subscribe((error: ImageProcessingError<unknown>) => {
            const { photo } = error;
            uploadModule.uploadError({
                sessionId: photo.sessionId,
                file: photo.file,
                error,
            });
        });
        uploadService.progress$.subscribe(({ photoId, uploaded, overall, eventType }) => {
            if (eventType === 'begin') {
                uploadModule.updateStatus({ photoId, status: 'progress' });
            }

            uploadModule.updateProgress({ photoId, current: uploaded, overall });
        });
        uploadService.ids$.subscribe(({ photo: { sessionId, file }, id, overwrite }) => {
            uploadModule.setPlaceholderId({ sessionId, file, id });
            if (overwrite) {
                uploadModule.markOverwrite({ sessionId, file, overwriteId: id });
            }
        });
    },
};
